import Fab from '@/components/elements/forms/buttons/Fab/Fab';
import { usePinInput } from '@/components/elements/forms/PinInput/PinInput';
import Icon from '@/components/icons/Icon';
import { baseTranslationKey } from '@/components/modules/modals/LoginModal';
import CreateAccount from '@/components/templates/login/CreateAccount';
import RequestToken from '@/components/templates/login/RequestToken';
import VerifyOTP from '@/components/templates/login/VerifyOTP';
import VerifySuccess from '@/components/templates/login/VerifySuccess';
import { EVENT_NAME, SCREEN_VIEW } from '@/constants/analytics';
import { trackMpEvent } from '@/helpers';
import { useAppSelector } from '@/hooks';
import { LoginContext } from '@/hooks/useLogin';
import useTrackScreenShown from '@/hooks/useTrackScreenShown';
import { _s } from '@/locale';
import { TriggerSource } from '@/types/analytics';

import { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Fragment } from 'react/jsx-runtime';

const source: TriggerSource = 'top_navigation_bar';

const LoginPopover = () => {
  const context = useContext(LoginContext);
  const pinProps = usePinInput({ fields: 6, validateCallback: context.handleLogin });
  const user = useAppSelector((state) => state.users?.user);
  const { pathname } = useLocation();
  const { userProfileOnboarding } = useAppSelector((state) => state.modal);

  useEffect(() => {
    if (user) context.updateView('verifySuccess');
    if (context.view === 'verifySuccess' && !userProfileOnboarding.show)
      trackMpEvent(EVENT_NAME.LOGIN_LOGGED_IN, { screen_name: SCREEN_VIEW.LOGIN_START, initiated_from: source });
  }, [user, context.view]);

  useTrackScreenShown(
    { screen_name: SCREEN_VIEW.LOGIN_START, initiated_from: source },
    [context.view],
    [context.view === 'requestToken'],
  );

  useEffect(() => {
    if (pinProps.error || pinProps.clipError) {
      trackMpEvent(EVENT_NAME.LOGIN_FAILED, {
        screen_name: SCREEN_VIEW.LOGIN_START,
        initiated_from: source,
      });
    }
  }, [pinProps.error, pinProps.clipError]);

  const contentView = (() => {
    switch (context.view) {
      case 'requestToken':
        return <RequestToken {...context} source={source} redirectUrl={pathname} autoFocus />;
      case 'createAccount':
        return <CreateAccount {...context} source={source} redirectUrl={pathname} />;
      case 'verifyOtp':
        return <VerifyOTP {...context} source={source} {...pinProps} />;
      case 'verifySuccess':
        return (
          <VerifySuccess
            {...context}
            onAnimationComplete={() => {
              context.onClose();
              context.clearLoginState();
            }}
          />
        );
    }
  })();

  const contentHeader = (() => {
    switch (context.view) {
      case 'requestToken':
        return (
          <Fragment>
            <div className="gap-lg pb-md flex items-start justify-between">
              <div className="bg-bg_info_bold grid place-content-center rounded-full" style={{ width: 56, height: 56 }}>
                <Icon variant="account" color="fg_alwaysLight" size="md" />
              </div>
            </div>

            <h2 className="text-fg_primary text-h-m">{_s(`${baseTranslationKey(context.view)}.title`)}</h2>
          </Fragment>
        );
      case 'createAccount':
        return (
          <Fragment>
            <div className="gap-lg flex items-start">
              <Fab
                onClick={() => context.updateView('requestToken')}
                variant="secondary"
                icon={<Icon variant="back" />}
                size="md"
                aria-label={_s('back')}
              />
            </div>
            <h2 className="text-fg_primary text-h-m">{_s(`${baseTranslationKey(context.view)}.title`)}</h2>
          </Fragment>
        );
      case 'verifyOtp':
        return (
          <Fragment>
            <div className="gap-lg flex items-start">
              <Fab
                onClick={() => context.updateView('requestToken')}
                variant="secondary"
                icon={<Icon variant="back" />}
                size="md"
                aria-label={_s('back')}
              />
            </div>
            <h2 className="text-fg_primary text-h-m">{_s(`${baseTranslationKey(context.view)}.title`)}</h2>
          </Fragment>
        );
      case 'verifySuccess':
        return <h2 className="text-fg_primary text-h-m">{_s(`${baseTranslationKey(context.view)}.title`)}</h2>;
    }
  })();

  return (
    <div className="bg-bg_neutral border-border_bold p-xl gap-md flex w-[340px] flex-col rounded-md border drop-shadow-lg">
      {contentHeader}
      {contentView}
    </div>
  );
};

export default LoginPopover;
